import React, { useCallback, useState } from 'react'
import { Icon, SemanticICONS } from 'semantic-ui-react'
import classnames from 'classnames'
import './MobilePanelNav.scss'

// The four phone views. Three map to the desktop sheet columns; `board` clears
// the panel overlay so the Godot board underneath is fully visible/interactive.
export type MobilePanel = 'status' | 'turn' | 'finance' | 'board'

interface Tab {
  key: MobilePanel
  label: string
  icon: SemanticICONS
}

const TABS: Tab[] = [
  { key: 'status', label: 'Players', icon: 'users' },
  { key: 'turn', label: 'Turn', icon: 'rocket' },
  { key: 'finance', label: 'Finance', icon: 'dollar sign' },
  { key: 'board', label: 'Board', icon: 'globe' },
]

interface MobilePanelNavProps {
  active: MobilePanel
  onSelect: (panel: MobilePanel) => void
  // Tabs that want the player's attention (e.g. a trade window opened, or the
  // game is waiting on their move). Shown as a dot unless the tab is active.
  attention?: Partial<Record<MobilePanel, boolean>>
  // Live cash readout rendered as a fifth chip.
  cashLabel?: string
  // Action for tapping the cash chip — the same balance-cash sweep as the
  // SHIP panel's Cash button. The chip disables while the promise is in
  // flight (mirroring PromiseButtonV2). Without it, the chip falls back to
  // opening the Finance tab.
  onCashTap?: () => Promise<unknown> | unknown
  // Paint the chip in the danger color when the player is in the red, like
  // the SHIP panel's Cash button does.
  cashNegative?: boolean
}

export const MobilePanelNav = ({ active, onSelect, attention, cashLabel, onCashTap, cashNegative }: MobilePanelNavProps) => {
  const [cashBusy, setCashBusy] = useState(false)

  const handleCashTap = useCallback(async () => {
    if (!onCashTap) {
      onSelect('finance')
      return
    }
    if (cashBusy) return
    setCashBusy(true)
    try {
      await onCashTap()
    } catch {
      // The action owner surfaces errors (GameSheet pushes a toast); the
      // chip only needs to re-enable.
    } finally {
      setCashBusy(false)
    }
  }, [onCashTap, onSelect, cashBusy])

  return (
    <nav className="pg-mobile-nav" role="tablist" aria-label="Game panels">
      {TABS.map(tab => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          className={classnames('pg-mobile-nav__tab', { 'pg-mobile-nav__tab--active': active === tab.key })}
          onClick={() => onSelect(tab.key)}
        >
          <span className="pg-mobile-nav__iconwrap">
            <Icon name={tab.icon} className="pg-mobile-nav__icon" />
            {attention?.[tab.key] && active !== tab.key && (
              <span className="pg-mobile-nav__dot" aria-label="Needs attention" />
            )}
          </span>
          <span className="pg-mobile-nav__label">{tab.label}</span>
        </button>
      ))}
      {cashLabel !== undefined && (
        <button
          type="button"
          className={classnames('pg-mobile-nav__tab pg-mobile-nav__cash', {
            'pg-mobile-nav__cash--busy': cashBusy,
            'pg-mobile-nav__cash--negative': cashNegative,
          })}
          disabled={cashBusy}
          onClick={handleCashTap}
          aria-label={onCashTap ? `Cash ${cashLabel} — balance cash accounts` : `Cash ${cashLabel} — open finance`}
        >
          <span className="pg-mobile-nav__iconwrap">
            <Icon name="money bill alternate outline" className="pg-mobile-nav__icon" />
          </span>
          <span className="pg-mobile-nav__label">{cashLabel}</span>
        </button>
      )}
    </nav>
  )
}

export default MobilePanelNav
